import { GLContext } from "../rendering/gl_context.js";
import { InstanceBatch } from "../batching/instance_batch.js";
import { IRenderLayerStats } from "../types/webgl_types.js";

export interface IDirtyRect
{
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * WebGLUIRenderLayer — окремий шар для рендеру UI елементів (наприклад, пазлів).
 * Працює з `preserveDrawingBuffer: true` і використовує `gl.scissor` для часткового перемалювання "брудних зон".
 */
export class WebGLUIRenderLayer
{
  public readonly canvas: HTMLCanvasElement;
  public readonly gl_ctx: GLContext;
  
  private readonly elements: InstanceBatch[] = [];
  private readonly element_map: Map<Function, InstanceBatch> = new Map();
  
  private dirty_rects: IDirtyRect[] = [];
  private dirty_rects_count: number = 0;
  private clustered_rects: IDirtyRect[] = [];
  private clustered_rects_count: number = 0;
  private last_update_time: number = 0;
  
  private needs_full_redraw: boolean = true;

  constructor( canvas: HTMLCanvasElement )
  {
    this.canvas = canvas;

    const gl = this.canvas.getContext( "webgl2", { 
      alpha: true, 
      preserveDrawingBuffer: true, 
      antialias: false, 
      depth: false 
    } );
    
    if ( !gl )
    {
      throw new Error(
        "Failed to initialize WebGL2 context for WebGL UI render layer. " +
        "Your browser or device may not support WebGL2."
      );
    }
    this.gl_ctx = new GLContext( gl );
  }

  public resize(): void
  {
    this.gl_ctx.gl.viewport( 0, 0, this.canvas.width, this.canvas.height );
    this.requestFullRedraw();
  }

  public register<T extends InstanceBatch>( element: T ): T
  {
    element.init( this.gl_ctx.gl );
    this.elements.push( element );
    this.element_map.set( element.constructor, element );
    return element;
  }

  public get<T extends InstanceBatch>( ctor: new ( ...args: any[] ) => T ): T
  {
    const found = this.element_map.get( ctor );
    if ( !found )
      throw new Error( `Element ${ctor.name} is not registered in WebGLUIRenderLayer` );
    return found as T;
  }

  public addDirtyRect( x: number, y: number, w: number, h: number ): void
  {
    if ( this.dirty_rects_count < this.dirty_rects.length )
    {
      const rect = this.dirty_rects[ this.dirty_rects_count ];
      rect.x = x;
      rect.y = y;
      rect.w = w;
      rect.h = h;
    }
    else
      this.dirty_rects.push( { x, y, w, h } );

    this.dirty_rects_count++;
  }

  public requestFullRedraw(): void
  {
    this.needs_full_redraw = true;
  }

  public update( now: number ): void
  {
    this.last_update_time = now;
    const gl = this.gl_ctx.gl;
    for ( const e of this.elements )
      e.updateBuffer( now, gl );
  }

  public render( dpr: number ): void
  {
    if ( !this.needs_full_redraw && this.dirty_rects_count === 0 )
      return;

    const gl = this.gl_ctx.gl;
    this.gl_ctx.updateGlobalParams( dpr, this.last_update_time );
    
    if ( this.needs_full_redraw )
    {
      gl.disable( gl.SCISSOR_TEST );
      this.gl_ctx.clear();
      for ( const e of this.elements )
        e.render( gl );
      this.needs_full_redraw = false;
    }
    else
    {
      gl.enable( gl.SCISSOR_TEST );
      
      const canvas_h = this.canvas.height;
      
      this.clustered_rects_count = 0;
      const padding = 20;

      for ( let i = 0; i < this.dirty_rects_count; ++i )
      {
        const rect = this.dirty_rects[ i ];
        let merged_into_idx = -1;
        
        for ( let j = 0; j < this.clustered_rects_count; ++j )
        {
          const cluster = this.clustered_rects[ j ];
          if (
            rect.x < cluster.x + cluster.w + padding &&
            rect.x + rect.w + padding > cluster.x &&
            rect.y < cluster.y + cluster.h + padding &&
            rect.y + rect.h + padding > cluster.y
          )
          {
             merged_into_idx = j;
             break;
          }
        }
        
        if ( merged_into_idx !== -1 )
        {
           const cluster = this.clustered_rects[ merged_into_idx ];
           const min_x = Math.min( cluster.x, rect.x );
           const min_y = Math.min( cluster.y, rect.y );
           const max_x = Math.max( cluster.x + cluster.w, rect.x + rect.w );
           const max_y = Math.max( cluster.y + cluster.h, rect.y + rect.h );
           cluster.x = min_x;
           cluster.y = min_y;
           cluster.w = max_x - min_x;
           cluster.h = max_y - min_y;
        }
        else
        {
           if ( this.clustered_rects_count < this.clustered_rects.length )
           {
             const cluster = this.clustered_rects[ this.clustered_rects_count ];
             cluster.x = rect.x;
             cluster.y = rect.y;
             cluster.w = rect.w;
             cluster.h = rect.h;
           }
           else
             this.clustered_rects.push( { x: rect.x, y: rect.y, w: rect.w, h: rect.h } );

           this.clustered_rects_count++;
        }
      }
      
      let changed = true;
      while ( changed && this.clustered_rects_count > 1 )
      {
        changed = false;
        for ( let i = 0; i < this.clustered_rects_count; ++i )
        {
          for ( let j = i + 1; j < this.clustered_rects_count; ++j )
          {
            const rect_a = this.clustered_rects[ i ];
            const rect_b = this.clustered_rects[ j ];
            if (
              rect_a.x < rect_b.x + rect_b.w + padding &&
              rect_a.x + rect_a.w + padding > rect_b.x &&
              rect_a.y < rect_b.y + rect_b.h + padding &&
              rect_a.y + rect_a.h + padding > rect_b.y
            )
            {
               const min_x = Math.min( rect_a.x, rect_b.x );
               const min_y = Math.min( rect_a.y, rect_b.y );
               const max_x = Math.max( rect_a.x + rect_a.w, rect_b.x + rect_b.w );
               const max_y = Math.max( rect_a.y + rect_a.h, rect_b.y + rect_b.h );
               rect_a.x = min_x;
               rect_a.y = min_y;
               rect_a.w = max_x - min_x;
               rect_a.h = max_y - min_y;
               
               const last_rect = this.clustered_rects[ this.clustered_rects_count - 1 ];
               rect_b.x = last_rect.x;
               rect_b.y = last_rect.y;
               rect_b.w = last_rect.w;
               rect_b.h = last_rect.h;
               this.clustered_rects_count--;
               changed = true;
               break;
            }
          }
          if ( changed )
            break;
        }
      }
      
      for ( let i = 0; i < this.clustered_rects_count; ++i )
      {
        const cluster = this.clustered_rects[ i ];
        if ( cluster.w <= 0 || cluster.h <= 0 )
          continue;
        
        const rect_x = Math.floor( cluster.x * dpr );
        const rect_w = Math.ceil( cluster.w * dpr );
        const rect_h = Math.ceil( cluster.h * dpr );
        const rect_y = Math.floor( canvas_h - ( ( cluster.y + cluster.h ) * dpr ) );

        gl.scissor( rect_x, rect_y, rect_w, rect_h );
        this.gl_ctx.clear();
        
        for ( const e of this.elements )
          e.render( gl );
      }
      
      gl.disable( gl.SCISSOR_TEST );
    }

    this.dirty_rects_count = 0; // Очищуємо лічильник (без GC аллокацій)
    this.gl_ctx.restoreDefaults();
  }

  public hasActiveElements(): boolean
  {
    for ( const e of this.elements )
      if ( e.hasInstances() )
        return true;
    return false;
  }

  public getStats(): IRenderLayerStats
  {
    let active = 0;
    let pending = 0;
    for ( const e of this.elements )
    {
      active += e.getActiveCount();
      pending += e.getPendingCount();
    }
    return { active, pending };
  }

  public clearAll(): void
  {
    for ( const e of this.elements )
      e.killAll();
    this.dirty_rects_count = 0;
    this.requestFullRedraw();
  }

  public dispose(): void
  {
    const gl = this.gl_ctx.gl;
    for ( const e of this.elements )
      e.dispose( gl );
    this.gl_ctx.dispose();
    this.canvas.remove();
  }
}
